import React, { useCallback, useEffect, useState } from "react";
import { Form, Space } from "antd";
import {
  FormButtons,
  FormInputText,
  FormInputPassword,
  FormInputDate,
} from "components";
import { Formik, useFormik } from "formik";
import { findCountiesRequest } from "services/county";
import { findParishesRequest } from "services/parish";
import { findMaritalStatusRequest } from "services/user";
import styled from "styled-components";
import { FormDropdown } from "common";
import useFetch from "hooks/useFetch";
import { estados, sexo } from "services/data";
import Filters from "utils/Filters";
import { UserCredentialsValidationSchema } from "./ValidationSchema";
import { getDiocesesByUf } from "services/diocese";
import { Dates } from "utils";
import { CreateChargeUser } from "../Edit/components/CreateCharge";

const CustomSpace = styled(Space)`
  width: 100%;
  .ant-space-item {
    width: 100%;
  }
`;

const UserForm = ({ handleSubmitForm, initialValues, handleCancel }) => {
  const [{ data: maritalStatus }, fetch] = useFetch(
    findMaritalStatusRequest,
    "",
    true,
    []
  );
  const [{ data: counties }] = useFetch(findCountiesRequest, "", true, []);
  const [{ data: parishes }, fetchParishes] = useFetch(
    findParishesRequest,
    "",
    false,
    [],
    null,
    (res) => {
      res.data.length && verifyIsAnotherParish(res.data)
    }
  );
  const formik = useFormik({
    onSubmit: handleSubmitForm,
    initialValues: initialValues,
    validationSchema: UserCredentialsValidationSchema,
  })

  const [dioceses, setDioceses] = useState([]);
  const [isAnotherParish, setIsAnotherParish] = useState(false);

  const getDioceses = useCallback(async (uf) => {
    try {
      const res = await getDiocesesByUf(uf);
      setDioceses(res.data);
    } catch (error) {
      console.log(81, error);
    }
  }, [initialValues])

  const verifyIsAnotherParish = useCallback((data) => {
    const comarca_id = data[0].comarca_id;
    const findCounties = counties.find(countie => countie.id === comarca_id);

    if (findCounties?.nm_comarca === 'Outra') {
      const findparishes = data?.find(parishe => parishe?.nm_paroquia === 'Outra')
      findparishes?.id && formik.setFieldValue('paroquia_id', findparishes.id)
    }

    setIsAnotherParish(findCounties?.nm_comarca === 'Outra')
  }, [counties, parishes]);

  const handleChange = (name, value) => {
    formik.setFieldValue(name, value);
  }

  useEffect(() => {
    if (initialValues && initialValues.comarca_id) {
      fetchParishes(initialValues.comarca_id);
    }

    if (initialValues && initialValues.sg_uf){
      getDioceses(initialValues.sg_uf)
    }
  }, [initialValues, counties]);

  return (
    <Formik
      initialValues={formik.initialValues}
      onSubmit={formik.handleSubmit}
      validationSchema={formik.validationSchema}
    >
      {({ handleSubmit, values, setFieldValue, errors }) => (
        <Form onSubmit={formik.handleSubmit}>
          <CustomSpace direction="horizontal">
            <FormInputText
              required
              name="username"
              placeholder="Nome"
              error={formik.errors}
              touched={formik.touched}
              value={formik.values.username}
              onChange={e => handleChange('username', e)}
            />

            <FormInputText
              name="email"
              disabledNoColor
              placeholder="E-mail"
              error={formik.errors}
              touched={formik.touched}
              value={formik.values.email}
              disabled={formik.values.id}
              onChange={e => handleChange('email', e)}
            />

            <FormDropdown
              list={sexo}
              name="ds_sexo"
              placeholder="Sexo"
              error={formik.errors}
              touched={formik.touched}
              value={formik.values.ds_sexo}
              onChange={e => handleChange('ds_sexo', e)}
            />
          </CustomSpace>

          <CustomSpace direction="horizontal">
            <FormInputText
              required
              name="nu_cpf"
              placeholder="CPF"
              error={formik.errors}
              touched={formik.touched}
              onChange={e => handleChange('nu_cpf', e)}
              value={Filters.inputMaskCPF(formik.values.nu_cpf)}
            />

            <FormInputText
              name="nu_celular"
              error={formik.errors}
              touched={formik.touched}
              placeholder="Número celular"
              onChange={e => handleChange('nu_celular', e)}
              value={Filters.inputMaskCellphone(formik.values.nu_celular)}
            />

            <FormInputDate
              noMargin
              name="dt_nascimento"
              error={formik.errors}
              touched={formik.touched}
              placeholder="Data de Nascimento"
              value={formik.values.dt_nascimento}
              onChangeParam={e => handleChange('dt_nascimento', e)}
            />
          </CustomSpace>

          <CustomSpace direction="horizontal">
            <FormDropdown
              propertyValue="id"
              error={formik.errors}
              name="estado_civil_id"
              touched={formik.touched}
              placeholder="Estado Civil"
              list={maritalStatus || []}
              propertyLabel="ds_estado_civil"
              value={formik.values.estado_civil_id}
              onChange={e => handleChange('ds_estado_civil', e)}
            />

            <FormDropdown
              name="comarca_id"
              propertyValue="id"
              placeholder="Comarca"
              list={counties || []}
              error={formik.errors}
              touched={formik.touched}
              propertyLabel="nm_comarca"
              value={formik.values.comarca_id}
              onChange={(e) => {
                handleChange('comarca_id', e)
                formik.setFieldValue("paroquia_id", null);
                fetchParishes(e);
              }}
            />

            {
              isAnotherParish ? (
                <FormInputText
                  required
                  error={formik.errors}
                  name="another_parish"
                  placeholder="Paróquia"
                  touched={formik.touched}
                  value={formik.values.another_parish}
                  onChange={e => handleChange('another_parish', e)}
                />
              ) : (
                <FormDropdown
                  name="paroquia_id"
                  propertyValue="id"
                  list={parishes || []}
                  error={formik.errors}
                  placeholder="Paróquia"
                  touched={formik.touched}
                  propertyLabel="nm_paroquia"
                  value={formik.values.paroquia_id}
                  disabled={!formik.values.comarca_id}
                  onChange={e => handleChange('paroquia_id', e)}
                />
              )
            }
          </CustomSpace>

          <CustomSpace direction="horizontal">
            <FormInputText
              name="nu_cep"
              placeholder="CEP"
              error={formik.errors}
              touched={formik.touched}
              value={Filters.inputMaskCEP(formik.values.nu_cep)}
              onChange={e => handleChange('nu_cep', e)}
            />

            <FormDropdown
              name="sg_uf"
              list={estados}
              placeholder="UF"
              error={formik.errors}
              touched={formik.touched}
              value={formik.values.sg_uf}
              onChange={e => {
                getDioceses(e)
                handleChange('sg_uf', e)
                formik.setFieldValue('id_diocese', undefined)
              }}
            />

            <FormInputText
              name="nm_cidade"
              placeholder="Cidade"
              error={formik.errors}
              touched={formik.touched}
              value={formik.values.nm_cidade}
              onChange={e => handleChange('nm_cidade', e)}
            />

            <FormInputText
              name="ds_endereco"
              error={formik.errors}
              touched={formik.touched}
              value={formik.values.ds_endereco}
              placeholder="Endereço ( Rua, Av, etc )"
              onChange={e => handleChange('ds_endereco', e)}
            />
          </CustomSpace>

          <CustomSpace direction="horizontal">
            <FormInputText
              required
              name="nr_endereco"
              placeholder="Número"
              error={formik.errors}
              touched={formik.touched}
              value={formik.values.nr_endereco}
              onChange={e => handleChange('nr_endereco', e)}
            />

            <FormInputText
              name="ds_complemento"
              placeholder="Complemento"
              error={formik.errors}
              touched={formik.touched}
              value={formik.values.ds_complemento}
              onChange={e => handleChange('ds_complemento', e)}
            />

            <FormInputText
              required
              name="nm_bairo"
              placeholder="Bairro"
              error={formik.errors}
              touched={formik.touched}
              value={formik.values.nm_bairo}
              onChange={e => handleChange('nm_bairo', e)}
            />
          </CustomSpace>

          <CustomSpace direction="horizontal">
            <FormDropdown
              list={dioceses}
              name="id_diocese"
              propertyValue='id'
              placeholder="Diocese"
              error={formik.errors}
              propertyLabel='diocese'
              touched={formik.touched}
              value={formik.values.id_diocese}
              onChange={e => handleChange('id_diocese', e)}
            />

            <FormInputPassword
              placeholder={
                formik.values.id
                  ? "Senha ( só altere este campo caso tenha necessidade )"
                  : "Senha"
              }
              name="password"
              error={formik.errors}
              touched={formik.touched}
              required={!formik.values.id}
              value={formik.values.password}
              onChange={e => handleChange('password', e)}
            />
          </CustomSpace>

          <CreateChargeUser idUser={formik.values.id} />

          <FormButtons
            bt2={{ label: "Salvar", onClick: handleSubmit }}
            bt1={{ label: "Cancelar", onClick: handleCancel }}
          />
        </Form>
      )}
    </Formik>
  );
};

export default UserForm;
